import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SectionShell } from "@/pages/SectionShell";
import { Button } from "@/components/Button";
import { EmptyState } from "@/components/EmptyState";
import { Loaded } from "@/components/Loading";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useCallable } from "@/lib/hooks/useCallable";
import { useGroups, usePeople } from "@/lib/hooks/usePeople";
import { CALLABLES } from "@/services/callables";
import { formatDateTime } from "@/lib/format";
import { lessonKind, lessonKindKey } from "./beats";
import type { Session, SessionMode } from "@/types";

/**
 * Lessons that have happened.
 *
 * A teacher sees their own. An owner sees that a lesson took place and how long
 * it ran, and nothing about what happened inside it — the backend filters the
 * list by teacher, and the rules refuse the contents outright. The notice says
 * so rather than leaving an owner to discover an empty page.
 */

const LIVE_STATES = new Set(["lobby", "casting", "scene", "debrief"]);

export function SessionsPage() {
  const t = useT();
  const { locale } = useI18n();
  const { user } = useAuth();
  const { groups } = useGroups();
  const { people } = usePeople();

  const [groupId, setGroupId] = useState("all");
  const [mode, setMode] = useState<SessionMode | "all">("all");

  const sessions = useCallable<{ sessions: Session[] }>(CALLABLES.listSessions);

  const groupName = useMemo(() => {
    const names = new Map(groups.map((group) => [group.id, group.name]));
    return (id: string) => names.get(id) ?? id;
  }, [groups]);

  const teacherName = useMemo(() => {
    const names = new Map(people.map((person) => [person.uid, person.displayName]));
    return (uid: string) => names.get(uid) ?? uid;
  }, [people]);

  const isOwner = user?.role === "owner";

  return (
    <SectionShell titleKey="sessions.title">
      {isOwner ? (
        <p className="surface-inset max-w-2xl px-4 py-3 text-sm text-ink-soft">
          {t("sessions.ownerNotice")}
        </p>
      ) : null}

      <div className="mt-6 flex flex-wrap items-end gap-3">
        <label>
          <span className="field-label">{t("sessions.filter.group")}</span>
          <select
            className="field-input"
            value={groupId}
            onChange={(event) => setGroupId(event.target.value)}
          >
            <option value="all">{t("people.filter.all")}</option>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span className="field-label">{t("sessions.filter.mode")}</span>
          <select
            className="field-input"
            value={mode}
            onChange={(event) => setMode(event.target.value as SessionMode | "all")}
          >
            <option value="all">{t("people.filter.all")}</option>
            <option value="online">{t("institution.mode.online")}</option>
            <option value="offline">{t("institution.mode.offline")}</option>
          </select>
        </label>
      </div>

      <div className="mt-6">
        <Loaded state={sessions}>
          {(loaded) => {
            const shown = loaded.sessions.filter((session) => {
              if (groupId !== "all" && session.groupId !== groupId) return false;
              if (mode !== "all" && session.mode !== mode) return false;
              return true;
            });

            /*
              A filter that matches nothing is not an empty history. The two
              were the same sentence — "No lesson has been run yet" under a
              filter for a group that has run twenty (live run, 2026-09-09) —
              and a teacher who filtered and forgot would conclude their term
              never happened.
            */
            const filtered = groupId !== "all" || mode !== "all";

            if (shown.length === 0 && filtered) {
              return (
                <EmptyState
                  titleKey="sessions.noMatch"
                  bodyKey="sessions.noMatchBody"
                  action={
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setGroupId("all");
                        setMode("all");
                      }}
                    >
                      {t("sessions.clearFilters")}
                    </Button>
                  }
                />
              );
            }

            if (shown.length === 0) {
              return <EmptyState titleKey="sessions.empty" bodyKey="sessions.emptyBody" />;
            }

            return (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-[14px]">
                  <caption className="sr-only">{t("sessions.title")}</caption>
                  <thead>
                    <tr className="border-b border-line bg-surface-muted text-left text-ink-muted">
                      <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("sessions.column.date")}</th>
                      <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("sessions.column.group")}</th>
                      <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("sessions.column.teacher")}</th>
                      <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("sessions.column.attendance")}</th>
                      <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("sessions.column.mode")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((session) => {
                      const present = session.register.filter((entry) => entry.present).length;
                      const kind = lessonKind(session);
                      return (
                        <tr key={session.id} className="border-b border-line last:border-0">
                          <td className="h-10 px-3 py-2.5">
                            <Link
                              to={`/sessions/${session.id}`}
                              className="text-ink underline-offset-2 hover:underline"
                            >
                              {formatDateTime(locale, session.startedAt)}
                            </Link>
                            {/* Product green: a lesson in the room, right now. */}
                            {LIVE_STATES.has(session.state) ? (
                              <span className="ml-2 text-cue-green">{t("sessions.live")}</span>
                            ) : null}
                            {/* A lesson with no scene says what it was instead. */}
                            {kind ? (
                              <span className="ml-2 text-ink-muted">{t(lessonKindKey(kind))}</span>
                            ) : null}
                          </td>
                          <td className="h-10 px-3 py-2.5 text-ink-muted">{groupName(session.groupId)}</td>
                          <td className="h-10 px-3 py-2.5 text-ink-muted">
                            {teacherName(session.teacherUid)}
                          </td>
                          <td className="h-10 px-3 py-2.5 text-ink-muted">
                            {t("sessions.attendance", {
                              present,
                              total: session.register.length,
                            })}
                          </td>
                          <td className="h-10 px-3 py-2.5 text-ink-muted">
                            {t(`institution.mode.${session.mode}`)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          }}
        </Loaded>
      </div>
    </SectionShell>
  );
}
